'use client';

import { motion, useReducedMotion } from 'motion/react';

/**
 * lucide's `Bot`, with the eyes given their own motion — the same two gestures animate-ui's icon
 * pack drives that mark with: the pupils track a small loop, and every few seconds it blinks.
 *
 * Hand-rolled rather than pulled in: their version needs the `animate-ui/icons` runtime
 * (`IconWrapper`, `useAnimateIconContext`, `getVariants`) and a hover/controls context, which is a
 * lot of machinery for one icon on one page that animates on its own.
 */
export function AnimatedBot({ className }: { className?: string }) {
  const reducedMotion = useReducedMotion();

  const eye = reducedMotion
    ? {}
    : {
        // animate-ui's own numbers: the `default` look-around, then the `blink`. Two transitions
        // sharing a cycle rather than one keyframe list, so the blink lands after the look.
        animate: { x: [0, -1.5, 1.5, 0], y: [0, 1.5, 1.5, 0], scaleY: [1, 0.5, 1] },
        transition: {
          x: { duration: 1.3, ease: 'easeInOut' as const, repeat: Infinity, repeatDelay: 2.1 },
          y: { duration: 1.3, ease: 'easeInOut' as const, repeat: Infinity, repeatDelay: 2.1 },
          scaleY: {
            duration: 0.6,
            delay: 1.7,
            ease: 'easeInOut' as const,
            repeat: Infinity,
            repeatDelay: 2.8,
          },
        },
      };

  // transform-box: fill-box is load-bearing. Without it an SVG child scales and translates about
  // the viewBox origin, so `scaleY` on a 2px eye swings it clear of the head.
  const eyeOrigin = { transformBox: 'fill-box', transformOrigin: 'center' } as const;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      <path d="M12 8V4H8" />
      <rect width={16} height={12} x={4} y={8} rx={2} />
      <path d="M2 14h2" />
      <path d="M20 14h2" />
      <motion.path d="M15 13v2" style={eyeOrigin} {...eye} />
      <motion.path d="M9 13v2" style={eyeOrigin} {...eye} />
    </svg>
  );
}
